import { faCircleX } from "@fortawesome/pro-solid-svg-icons/faCircleX";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CloseButton, Modal } from "@mantine/core";
import { useDisclosure, useHotkeys } from "@mantine/hooks";
import {
  useCallback,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

import { CANCEL_CONFIRMATION_PROPS } from "./assessment-question-field";
import {
  CompetencyModalContent,
  type CompetencyModalSaveMode,
} from "./competency-modal-content";
import { PostPublishContent } from "./post-publish";
import { ConfirmationPopover } from "@/components/ui/confirmation-popover";
import { useSelectiveClickOutside } from "@/hooks/use-selective-click-outside";
import type {
  AssessmentQuestionResponseFormat,
  ReviewPageCompetency,
} from "@/types/screener-builder";

interface CompetencyModalProps {
  /** Whether the screener already has the maximum number of written
   *  assessment questions. Disables the Written option in the format picker
   *  so voice questions can still be added at the written cap. */
  atMaxWrittenCompetencies?: boolean;
  competency?: ReviewPageCompetency;
  defaultResponseFormat?: AssessmentQuestionResponseFormat;
  isEdit: boolean;
  isLegacyScreener: boolean;
  isPublishedScreener: boolean;
  jobDescription: string;
  jobTitle: string;
  onClose: () => void;
  onCompetencyUpdate: Dispatch<SetStateAction<ReviewPageCompetency[]>>;
  onExitTransitionEnd: () => void;
  opened: boolean;
  otherCompetencies: ReviewPageCompetency[];
  readOnly: boolean;
  saveMode?: CompetencyModalSaveMode;
  /** Show the response-format picker (Written / Voice recommended / Voice
   *  required) when creating a new question — used by the unified
   *  Assessment Questions section. */
  showFormatPicker?: boolean;
}

export const CompetencyModal = ({
  atMaxWrittenCompetencies,
  competency,
  defaultResponseFormat,
  isEdit,
  isLegacyScreener,
  isPublishedScreener,
  jobDescription,
  jobTitle,
  onClose,
  onCompetencyUpdate,
  onExitTransitionEnd,
  opened,
  otherCompetencies,
  readOnly,
  saveMode,
  showFormatPicker,
}: CompetencyModalProps) => {
  const [contentKey, setContentKey] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [
    closeConfirmationOpen,
    { close: closeCloseConfirmation, open: openCloseConfirmation },
  ] = useDisclosure();
  const [
    cancelConfirmationOpen,
    { close: closeCancelConfirmation, open: openCancelConfirmation },
  ] = useDisclosure();

  const handleCloseAttempt = useCallback(() => {
    if (!opened) {
      return;
    }

    // Close cancel button's confirmation if open
    closeCancelConfirmation();

    if (!hasUnsavedChanges) {
      return onClose();
    }

    if (!closeConfirmationOpen) {
      openCloseConfirmation();
    }
  }, [
    opened,
    closeCancelConfirmation,
    hasUnsavedChanges,
    closeConfirmationOpen,
    onClose,
    openCloseConfirmation,
  ]);

  const handleConfirmedClose = useCallback(() => {
    closeCloseConfirmation();
    closeCancelConfirmation();
    onClose();
  }, [closeCloseConfirmation, closeCancelConfirmation, onClose]);

  const ref = useSelectiveClickOutside<HTMLDivElement>(
    handleCloseAttempt,
    "data-confirmation-popover",
    opened,
  );
  useHotkeys([["Escape", handleCloseAttempt]], []);

  // Modal is decomposed into its subcomponents to allow for placing a ref on
  // the <Modal.Content>
  return (
    <Modal.Root
      closeOnClickOutside={false}
      closeOnEscape={false}
      onClose={onClose}
      onExitTransitionEnd={() => {
        setContentKey((prev) => prev + 1);
        onExitTransitionEnd();
      }}
      opened={opened}
      size={850}
      yOffset={72}
    >
      <Modal.Overlay />
      <Modal.Content className="px-[30px] pb-[30px]" ref={ref}>
        <Modal.Header className="h-min min-h-min px-0 pb-[24px] pt-[30px]">
          <Modal.Title className="font-semibold text-2xl">
            {readOnly
              ? "Assessment question details"
              : `${isEdit ? "Edit" : "Add"} assessment question`}
          </Modal.Title>
          <ConfirmationPopover
            {...CANCEL_CONFIRMATION_PROPS}
            isOpen={closeConfirmationOpen}
            onClose={closeCloseConfirmation}
            onPrimaryButtonClick={handleConfirmedClose}
          >
            <CloseButton
              icon={
                <FontAwesomeIcon
                  className="bg-uw-gray-7 rounded-full text-xl text-uw-gray-0"
                  icon={faCircleX}
                />
              }
              onClick={handleCloseAttempt}
            />
          </ConfirmationPopover>
        </Modal.Header>
        <Modal.Body className="p-0">
          {readOnly ? (
            <PostPublishContent
              // Because read-only mode does not allow for the creation of new
              // competencies, the modal will only be opened when an existing
              // competency is selected
              competency={competency as ReviewPageCompetency}
              isLegacyScreener={isLegacyScreener}
              onClose={onClose}
            />
          ) : (
            <CompetencyModalContent
              key={contentKey}
              atMaxWrittenCompetencies={atMaxWrittenCompetencies}
              cancelConfirmationOpen={cancelConfirmationOpen}
              closeIconCloseConfirmation={closeCloseConfirmation}
              closeCancelConfirmation={closeCancelConfirmation}
              competency={competency}
              defaultResponseFormat={defaultResponseFormat}
              isPublishedScreener={isPublishedScreener}
              jobDescription={jobDescription}
              jobTitle={jobTitle}
              onClose={onClose}
              onCompetencyUpdate={onCompetencyUpdate}
              onUnsavedChangesChange={setHasUnsavedChanges}
              openCancelConfirmation={openCancelConfirmation}
              otherCompetencies={otherCompetencies}
              saveMode={saveMode}
              showFormatPicker={showFormatPicker}
            />
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
